<template>
	<v-container fluid class="pa-0">
		<v-card flat class="mb-4">
			<v-card-title class="d-flex justify-space-between align-center">
				<div>
					<h2 class="text-h5">Print Job History</h2>
					<p class="text-subtitle-2 mb-0">View and monitor all print jobs in the system</p>
				</div>
				<div class="d-flex align-center">
					<v-btn color="primary" class="mr-2" :loading="isRefreshing" @click="refreshJobs">
						<v-icon left>mdi-refresh</v-icon>
						Refresh
					</v-btn>
					<v-btn color="primary" :disabled="!selectedJobs.length" @click="retrySelectedJobs">
						<v-icon left>mdi-printer-check</v-icon>
						Retry Selected
					</v-btn>
				</div>
			</v-card-title>

			<v-card-text>
				<v-row>
					<v-col cols="12" sm="3">
						<v-text-field
							v-model="filters.documentNumber"
							label="Document Number"
							outlined
							dense
							clearable
							hide-details
							class="larger-input"
							@input="applyFilters"
						></v-text-field>
					</v-col>
					<v-col cols="12" sm="3">
						<v-select
							v-model="filters.documentType"
							:items="documentTypes"
							label="Document Type"
							outlined
							dense
							clearable
							class="larger-select"
							hide-details
							@change="applyFilters"
						></v-select>
					</v-col>
					<v-col cols="12" sm="3">
						<v-select
							v-model="filters.status"
							:items="statusOptions"
							label="Status"
							outlined
							dense
							clearable
							class="larger-select"
							hide-details
							@change="applyFilters"
						></v-select>
					</v-col>
					<v-col cols="12" sm="3">
						<v-menu
							ref="dateMenu"
							v-model="dateMenu"
							:close-on-content-click="false"
							transition="scale-transition"
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="dateRangeText"
									label="Date Range"
									readonly
									outlined
									dense
									clearable
									hide-details
									class="larger-input"
									v-bind="attrs"
									v-on="on"
									@click:clear="clearDateRange"
								></v-text-field>
							</template>
							<v-date-picker v-model="dateRange" range @change="applyFilters"></v-date-picker>
						</v-menu>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>

		<!-- Alert for errors -->
		<v-alert v-model="showError" dismissible type="error" class="mb-4">
			{{ errorMessage }}
		</v-alert>

		<v-data-table
			:headers="headers"
			:items="filteredJobs"
			:loading="loading"
			:server-items-length="totalJobs"
			:options.sync="options"
			item-key="printJobId"
			:footer-props="{
				itemsPerPageOptions: [10, 25, 50, 100],
				'items-per-page-text': 'Rows per page:',
				showFirstLastPage: true,
				prevIcon: 'mdi-chevron-left',
				nextIcon: 'mdi-chevron-right',
			}"
			v-model="selectedJobs"
			show-select
			class="elevation-1 improved-table"
			@update:options="handleOptionsUpdate"
		>
			<!-- Status Column -->
			<template v-slot:item.status="{ item }">
				<v-tooltip bottom :max-width="300">
					<template v-slot:activator="{ on, attrs }">
						<v-chip
							:color="getStatusColor(item.status)"
							text-color="white"
							small
							label
							v-bind="attrs"
							v-on="on"
						>
							<span v-if="!item.status">
								<v-icon small left>mdi-loading mdi-spin</v-icon>
								Loading
							</span>
							<span v-else>{{ getDisplayStatus(item.status) }}</span>
						</v-chip>
					</template>
					<span>{{ getDisplayStatus(item.status) }}</span>
				</v-tooltip>
			</template>

			<!-- Duration Column -->
			<template v-slot:item.totalDurationSeconds="{ item }">
				<v-tooltip bottom :max-width="300" :disabled="item.totalDurationSeconds == null">
					<template v-slot:activator="{ on, attrs }">
						<span v-bind="attrs" v-on="on" class="duration-text">
							{{ item.totalDurationSeconds != null ? formatDuration(item.totalDurationSeconds) : '--' }}
						</span>
					</template>
					<div>
						<div v-if="item.timeToSubmitSeconds != null">
							Render + submit: {{ formatDuration(item.timeToSubmitSeconds) }}
						</div>
						<div v-if="item.ezeepProcessingSeconds != null">
							Printer processing: {{ formatDuration(item.ezeepProcessingSeconds) }}
						</div>
					</div>
				</v-tooltip>
			</template>

			<!-- Document Type Column -->
			<template v-slot:item.documentType="{ item }">
				<v-chip :color="getDocumentTypeColor(item.documentType)" outlined small label>
					{{ getDocumentTypeName(item.documentType) }}
				</v-chip>
			</template>

			<!-- Document Number Column with Jump Links -->
			<template v-slot:item.documentNumber="{ item }">
				<a
					v-if="canNavigateToDocument(item)"
					href="#"
					@click.prevent="navigateToDocument(item)"
					class="document-link"
				>
					{{ item.documentNumber }}
				</a>
				<span v-else>{{ item.documentNumber }}</span>
			</template>

			<!-- Created At Column -->
			<template v-slot:item.createdAt="{ item }">
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<span v-bind="attrs" v-on="on">{{ formatDate(item.createdAt) }}</span>
					</template>
					<span>{{ formatDate(item.createdAt, true) }}</span>
				</v-tooltip>
			</template>

			<!-- Actions Column -->
			<template v-slot:item.actions="{ item }">
				<div class="d-flex">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small v-bind="attrs" v-on="on" @click="viewJobDetails(item)">
								<v-icon small>mdi-eye</v-icon>
							</v-btn>
						</template>
						<span>View Details</span>
					</v-tooltip>

					<v-tooltip bottom v-if="canReprint(item)">
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small color="success" v-bind="attrs" v-on="on" @click="reprintJob(item)">
								<v-icon small>mdi-printer</v-icon>
							</v-btn>
						</template>
						<span>Reprint Document</span>
					</v-tooltip>

					<v-tooltip bottom v-if="item.status && getDisplayStatus(item.status) === 'Failed'">
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="retryJob(item)">
								<v-icon small>mdi-reload</v-icon>
							</v-btn>
						</template>
						<span>Retry Print Job</span>
					</v-tooltip>
				</div>
			</template>

			<!-- No Data Template -->
			<template v-slot:no-data>
				<div class="text-center pa-6">
					<v-icon x-large class="mb-3 text--disabled">mdi-printer-off</v-icon>
					<p class="text-subtitle-1">No print jobs found</p>
					<v-btn color="primary" @click="refreshJobs">Refresh</v-btn>
				</div>
			</template>
		</v-data-table>

		<!-- Job Details Dialog -->
		<v-dialog v-model="jobDetailsDialog" max-width="700px">
			<v-card>
				<v-card-title class="headline">
					Print Job Details
					<v-spacer></v-spacer>
					<v-chip :color="getStatusColor(selectedJob?.status)" text-color="white" label>
						{{ getDisplayStatus(selectedJob?.status) }}
					</v-chip>
				</v-card-title>

				<v-card-text v-if="selectedJob">
					<v-row>
						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Document Type</v-list-item-subtitle>
									<v-list-item-title>{{
										getDocumentTypeName(selectedJob.documentType)
									}}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Document Number</v-list-item-subtitle>
									<v-list-item-title>{{ selectedJob.documentNumber }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Job ID</v-list-item-subtitle>
									<v-list-item-title>{{ selectedJob.printJobId }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Created At</v-list-item-subtitle>
									<v-list-item-title>{{
										formatDate(selectedJob.createdAt, true)
									}}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>User</v-list-item-subtitle>
									<v-list-item-title>{{
										selectedJob.user?.name || selectedJob.userId
									}}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Printer</v-list-item-subtitle>
									<v-list-item-title>{{
										selectedJob.printer?.name || selectedJob.printerId
									}}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Copies</v-list-item-subtitle>
									<v-list-item-title>{{ selectedJob.copies }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6" v-if="selectedJob.initiatedAt">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Initiated At</v-list-item-subtitle>
									<v-list-item-title>{{ formatDate(selectedJob.initiatedAt, true) }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="6" v-if="selectedJob.totalDurationSeconds != null">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Total Duration</v-list-item-subtitle>
									<v-list-item-title>{{ formatDuration(selectedJob.totalDurationSeconds) }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-divider class="mx-4 my-3"></v-divider>

						<v-col cols="12" v-if="selectedJob.message">
							<v-list-item>
								<v-list-item-content>
									<v-list-item-subtitle>Message</v-list-item-subtitle>
									<v-list-item-title>{{ selectedJob.message }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-col>

						<v-col cols="12" v-if="jobErrorDetails">
							<v-alert type="error" outlined dense class="mx-4 mb-2">
								<div class="text-subtitle-2 mb-1">Error Details</div>
								<div style="font-size: 12px; white-space: pre-wrap; word-break: break-word;">{{ jobErrorDetails }}</div>
							</v-alert>
						</v-col>

						<v-col cols="12" v-if="jobStatusHistory.length">
							<h3 class="text-subtitle-1 ml-4 mb-2">Status History</h3>
							<v-timeline dense>
								<v-timeline-item
									v-for="(status, index) in jobStatusHistory"
									:key="index"
									:color="status.isPhase ? getPhaseColor(status.statusCode) : getStatusColor(status.status)"
									:icon="status.isPhase ? getPhaseIcon(status.statusCode) : undefined"
									:small="!status.isPhase"
								>
									<v-list-item>
										<v-list-item-content>
											<v-list-item-title>
												<span>{{ status.isPhase ? getPhaseDisplayName(status.status) : getDisplayStatus(status.status) }}</span>
												<v-chip
													v-if="index > 0"
													x-small
													outlined
													class="ml-2"
													:color="getTimeDeltaColor(getTimeDiffSeconds(jobStatusHistory[index - 1], status))"
												>
													+{{ formatDuration(getTimeDiffSeconds(jobStatusHistory[index - 1], status)) }}
												</v-chip>
											</v-list-item-title>
											<v-list-item-subtitle>{{
												formatDate(status.timestamp, true)
											}}</v-list-item-subtitle>
										</v-list-item-content>
									</v-list-item>
								</v-timeline-item>
							</v-timeline>
						</v-col>
					</v-row>
				</v-card-text>

				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn
						text
						color="error"
						v-if="
							selectedJob && selectedJob.status && getDisplayStatus(selectedJob.status) === 'Failed'
						"
						@click="retryJob(selectedJob)"
					>
						<v-icon left>mdi-printer-check</v-icon>
						Retry Print Job
					</v-btn>
					<v-btn text color="primary" @click="checkCurrentJobStatus" :loading="checkingStatus">
						<v-icon left>mdi-refresh</v-icon>
						Check Status
					</v-btn>
					<v-btn text @click="jobDetailsDialog = false"> Close </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Snackbar for notifications -->
		<v-snackbar
			v-model="snackbar.show"
			:color="snackbar.color"
			:timeout="snackbar.timeout"
			top
			right
		>
			<span class="text-h6">{{ snackbar.text }}</span>
			<template v-slot:action="{ attrs }">
				<v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
			</template>
		</v-snackbar>
	</v-container>
</template>

<script>
import { mapGetters } from "vuex";
import gql from "graphql-tag";
import { createProvider } from "../../plugins/vue-apollo";
import { getPrintJobSignalR } from "@/services/PrintJobSignalRService";

export default {
	name: "PrinterStatus",

	data() {
		return {
			// Table data
			jobs: [],
			filteredJobs: [],
			loading: false,
			isRefreshing: false,
			totalJobs: 0,
			selectedJobs: [],
			selectedJob: null,

			// Error handling
			showError: false,
			errorMessage: "",

			// Apollo client
			apolloClient: null,

			// Fetch options
			options: {
				page: 1,
				itemsPerPage: 25,
				sortBy: ["createdAt"], // Always default sort by createdAt (a valid field)
				sortDesc: [true],
			},

			// Job details dialog
			jobDetailsDialog: false,
			jobStatusHistory: [],
			checkingStatus: false,

			// Filters
			filters: {
				documentNumber: "",
				documentType: null,
				status: null,
			},
			dateRange: [],
			dateMenu: false,

			// Headers
			headers: [
				{ text: "Document Type", value: "documentType", width: "11%" },
				{ text: "Document #", value: "documentNumber", width: "11%" },
				{ text: "Status", value: "status", width: "9%" },
				{ text: "Duration", value: "totalDurationSeconds", sortable: false, width: "8%" },
				{ text: "Message", value: "message", width: "18%" },
				{ text: "User", value: "userName", width: "11%" },
				{ text: "Printer", value: "printerName", width: "13%" },
				{ text: "Created", value: "createdAt", width: "13%" },
				{ text: "Actions", value: "actions", sortable: false, width: "6%" },
			],

			// SignalR subscription cleanup
			signalRUnsubscribe: null,

			// Document type mapping
			documentTypes: [
				{ text: "Quote", value: "QUOTE" },
				{ text: "Sales Order", value: "ORDER" },
				{ text: "Pick Ticket", value: "PICK_TICKET" },
				{ text: "Purchase Order", value: "PURCHASE_ORDER" },
				{ text: "Packing Slip", value: "PACKING_SLIP" },
				{ text: "Other", value: "OTHER" },
			],

			// Status options
			statusOptions: [
				{ text: "Processing", value: "Processing" },
				{ text: "Completed", value: "Completed" },
				{ text: "Error", value: "Error" },
				{ text: "Failed", value: "Failed" },
				// Status options can be expanded as needed
			],

			// Snackbar state
			snackbar: {
				show: false,
				text: "",
				color: "success", // success, error, info
				timeout: 3000,
			},
		};
	},

	computed: {
		...mapGetters(["getCurrentUser"]),

		jobErrorDetails() {
			if (!this.selectedJob || !this.jobStatusHistory.length) return null;

			// Find the last failed/error entry with details
			const errorEntries = this.jobStatusHistory
				.filter(s => {
					const status = (s.status || '').toLowerCase();
					return status.includes('fail') || status.includes('error') || status.includes('timeout') || s.isTerminalStatus;
				});

			// Collect error details from status history entries
			const details = [];
			for (const entry of errorEntries) {
				if (entry.detailsJson) {
					try {
						const parsed = JSON.parse(entry.detailsJson);
						if (parsed.error) details.push(parsed.error);
						else if (parsed.message) details.push(parsed.message);
						else if (parsed.reason) details.push(parsed.reason);
						else details.push(entry.detailsJson);
					} catch {
						details.push(entry.detailsJson);
					}
				}
				if (entry.statusString && !details.includes(entry.statusString)) {
					details.push(entry.statusString);
				}
			}

			// Also check the job's message field
			if (this.selectedJob.message && this.selectedJob.message.toLowerCase().includes('error')) {
				details.unshift(this.selectedJob.message);
			}

			// Check for stuck "Rendering" jobs (no terminal status after creation)
			const displayStatus = this.getDisplayStatus(this.selectedJob.status);
			if (displayStatus === 'Rendering' || displayStatus === 'Generating PDF') {
				const createdAt = new Date(this.selectedJob.createdAt);
				const now = new Date();
				const minutesAgo = (now - createdAt) / 60000;
				if (minutesAgo > 2 && details.length === 0) {
					details.push(`Job has been in "${displayStatus}" state for ${Math.round(minutesAgo)} minutes. This may indicate a rendering failure — check server logs for Puppeteer errors.`);
				}
			}

			return details.length > 0 ? details.join('\n') : null;
		},

		dateRangeText() {
			if (!this.dateRange || this.dateRange.length === 0) return "";
			if (this.dateRange.length === 1) return this.formatDate(this.dateRange[0]);
			return `${this.formatDate(this.dateRange[0])} - ${this.formatDate(this.dateRange[1])}`;
		},
	},

	created() {
		// Initialize Apollo client directly
		this.apolloClient = createProvider().defaultClient;

		this.fetchJobs();

		// Subscribe to real-time print job status updates via SignalR
		this.signalRUnsubscribe = getPrintJobSignalR().subscribe((event) => {
			this.handleSignalRUpdate(event);
		});
	},

	beforeDestroy() {
		// Unsubscribe from SignalR
		if (this.signalRUnsubscribe) {
			this.signalRUnsubscribe();
			this.signalRUnsubscribe = null;
		}
	},

	methods: {
		/**
		 * Display a snackbar notification
		 */
		showSnackbar(text, color = "info", timeout = 3000) {
			this.snackbar.text = text;
			this.snackbar.color = color;
			this.snackbar.timeout = timeout;
			this.snackbar.show = true;
		},
		/**
		 * Fetch print jobs with pagination, sorting, and filtering using GraphQL
		 */
		async fetchJobs() {
			// Prevent multiple concurrent fetches
			if (this.loading) {
				console.log("Fetch already in progress, skipping");
				return;
			}

			// Add a safety timeout to ensure loading state is cleared
			const loadingTimeout = setTimeout(() => {
				if (this.loading) {
					console.warn("Loading timeout reached - forcing loading state to complete");
					this.loading = false;
					this.showError = true;
					this.errorMessage = "Loading timeout reached. Please try refreshing.";
				}
			}, 20000); // 20 second timeout - increased for safety

			try {
				this.loading = true;
				this.showError = false;

				// Build GraphQL filter object based on current filters
				const filter = {};

				if (this.filters.documentNumber) {
					filter.documentNumber = { contains: this.filters.documentNumber };
				}

				if (this.filters.documentType) {
					filter.documentType = { eq: this.filters.documentType };
				}

				if (this.filters.status) {
					filter.currentStatus = { contains: this.filters.status };
				}

				// Add date range if selected
				if (this.dateRange && this.dateRange.length === 2) {
					filter.createdAt = {
						gte: new Date(this.dateRange[0]),
						lte: new Date(this.dateRange[1]),
					};
				}

				// Build sorting options
				let sortBy = this.options.sortBy[0] || "createdAt";
				const sortDirection = this.options.sortDesc[0] ? "DESC" : "ASC";

				// Define which fields are sortable client-side only
				const clientSideSortColumns = ["status", "userName", "printerName"];
				const useClientSideSorting = clientSideSortColumns.includes(sortBy);

				// For client-side sorting or invalid fields, use createdAt for server-side sorting
				if (useClientSideSorting) {
					console.log(`Using client-side sorting for ${sortBy}, fetching with createdAt sort`);
					// Always use createdAt for server-side sorting when we'll do client-side sorting after
					sortBy = "createdAt";
				} else {
					// Make sure we're only using valid backend fields for server-side sorting
					if (sortBy === "user.name") {
						sortBy = "userId";
					} else if (sortBy === "printer.name") {
						sortBy = "printerId";
					}
				}

				console.log(
					`Fetching print jobs with filter:`,
					filter,
					`sort by: ${sortBy} (${sortDirection})`
				);

				// Execute GraphQL query to get print jobs
				const { data, errors } = await this.apolloClient.query({
					query: gql`
						query GetPrintJobs(
							$skip: Int!
							$take: Int!
							$filter: PrintJobLogFilterInput
							$sortOrder: [PrintJobLogSortInput!]
						) {
							printJobs(skip: $skip, take: $take, where: $filter, order: $sortOrder) {
								items {
									printJobLogId
									documentType
									documentNumber
									printJobId
									printerId
									copies
									message
									userId
									createdAt
									initiatedAt
									documentId
									quoteId
									orderId
									pickTicketId
									purchaseOrderId
									status
									printerName
									userName
									totalDurationSeconds
									timeToSubmitSeconds
									ezeepProcessingSeconds
									printer {
										name
										location
									}
									user {
										id
										userName
										firstName
										lastName
									}
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
								totalCount
							}
						}
					`,
					variables: {
						skip: (this.options.page - 1) * this.options.itemsPerPage,
						take: this.options.itemsPerPage,
						filter: Object.keys(filter).length > 0 ? filter : null,
						sortOrder: [
							{
								[sortBy]: sortDirection,
							},
						],
					},
					fetchPolicy: "network-only",
					errorPolicy: "all",
				});

				// Handle GraphQL errors
				if (errors && errors.length > 0) {
					this.showError = true;
					let errorMsg = errors[0].message;

					// Handle specific sorting errors
					if (errorMsg.includes("does not exist on the type") && errorMsg.includes("SortInput")) {
						console.warn("Sorting error, falling back to createdAt:", errorMsg);
						// Force a retry with createdAt sort
						this.options.sortBy = ["createdAt"];
						this.loading = false;
						clearTimeout(loadingTimeout);
						this.fetchJobs();
						return;
					}

					this.errorMessage = "Error loading print jobs: " + errorMsg;
					this.jobs = [];
					this.filteredJobs = [];
					this.totalJobs = 0;
					console.error("GraphQL errors:", errors);
					this.loading = false;
					clearTimeout(loadingTimeout);
					return;
				}

				// Ensure data exists
				if (!data || !data.printJobs || !data.printJobs.items) {
					this.showError = true;
					this.errorMessage = "No data returned from server";
					this.jobs = [];
					this.filteredJobs = [];
					this.totalJobs = 0;
					this.loading = false;
					clearTimeout(loadingTimeout);
					return;
				}

				console.log("Successfully loaded", data.printJobs.items.length, "print jobs");

				// Process the jobs data
				let processedJobs = data.printJobs.items.map((job) => {
					return {
						...job,
						user: {
							name: job.user
								? `${job.user.firstName} ${job.user.lastName}`.trim()
								: `User ${job.userId}`,
						},
					};
				});

				// Store unfiltered jobs
				this.jobs = processedJobs;

				this.filteredJobs = [...this.jobs];
				this.totalJobs = data.printJobs.totalCount || 0;

				// Client-side sorting is now handled in applyClientSideSorting method
				// Check if we need to apply client-side sorting after loading
				const needsClientSideSorting = useClientSideSorting;

				if (needsClientSideSorting) {
					// Apply client-side sorting if needed
					this.applyClientSideSorting();
				}

				console.log(
					`Loaded ${processedJobs.length} jobs, filtered to ${this.filteredJobs.length} jobs`
				);

					this.loading = false;
				clearTimeout(loadingTimeout);
			} catch (error) {
				console.error("Error fetching print jobs:", error);
				this.showError = true;
				this.errorMessage =
					"Failed to fetch print job history: " + (error.message || "Unknown error");
				this.jobs = [];
				this.filteredJobs = [];
				this.totalJobs = 0;
				this.loading = false;
				clearTimeout(loadingTimeout);
			}
		},

		/**
		 * Force refresh the job list
		 */
		async refreshJobs() {
			// Add a safety timeout to ensure isRefreshing state is cleared
			const refreshingTimeout = setTimeout(() => {
				if (this.isRefreshing) {
					console.warn("Refreshing timeout reached - forcing refresh state to complete");
					this.isRefreshing = false;
					this.showError = true;
					this.errorMessage = "Refresh operation timed out. Please try again.";
				}
			}, 20000); // 20 second timeout - increased for safety

			try {
				this.isRefreshing = true;
				await this.fetchJobs();
			} catch (error) {
				console.error("Error during refresh:", error);
				this.showError = true;
				this.errorMessage = "Error refreshing print jobs: " + (error.message || "Unknown error");
			} finally {
				this.isRefreshing = false;
				clearTimeout(refreshingTimeout);
			}
		},

		/**
		 * Handle changes to table options (sorting, pagination, etc.)
		 */
		handleOptionsUpdate(options) {
			// Check if sorting changed and it's one of our client-side sorted columns
			const clientSideSortColumns = ["status", "userName", "printerName"];
			const sortBy = options.sortBy[0] || "createdAt";

			if (clientSideSortColumns.includes(sortBy)) {
				// Handle client-side sorting
				this.handleClientSideSorting(options);
			} else {
				// For server-side sorting, trigger a normal fetch
				this.fetchJobs();
			}
		},

		/**
		 * Handle client-side sorting for specific columns
		 */
		handleClientSideSorting(options) {
			// First, check if we need to load data
			if (this.jobs.length === 0) {
				// Data not loaded yet, do a server fetch first with default sorting
				const originalOptions = { ...this.options };
				this.options.sortBy = ["createdAt"];
				this.options.sortDesc = [true];

				// Save the requested sort options
				const requestedSortBy = options.sortBy[0];
				const requestedSortDesc = options.sortDesc[0];

				// Do the fetch, then apply client sorting after
				this.loading = true;
				this.fetchJobs().then(() => {
					// Restore the requested sort options
					this.options.sortBy = [requestedSortBy];
					this.options.sortDesc = [requestedSortDesc];

					// Apply client sorting
					this.applyClientSideSorting();
				});
				return;
			}

			// Apply client-side sorting to the existing data
			this.applyClientSideSorting();
		},

		/**
		 * Apply client-side sorting based on current options
		 */
		applyClientSideSorting() {
			const sortBy = this.options.sortBy[0] || "createdAt";
			const sortDirection = this.options.sortDesc[0] ? "DESC" : "ASC";

			console.log(`Applying client-side sorting: ${sortBy} ${sortDirection}`);

			if (sortBy === "status") {
				// Sort by status
				this.filteredJobs.sort((a, b) => {
					const statusA = this.getDisplayStatus(a.status).toUpperCase();
					const statusB = this.getDisplayStatus(b.status).toUpperCase();

					if (sortDirection === "ASC") {
						return statusA.localeCompare(statusB);
					} else {
						return statusB.localeCompare(statusA);
					}
				});
			} else if (sortBy === "userName") {
				// Sort by user name
				this.filteredJobs.sort((a, b) => {
					const nameA = (a.user?.name || a.userName || "").toUpperCase();
					const nameB = (b.user?.name || b.userName || "").toUpperCase();

					if (sortDirection === "ASC") {
						return nameA.localeCompare(nameB);
					} else {
						return nameB.localeCompare(nameA);
					}
				});
			} else if (sortBy === "printerName") {
				// Sort by printer name
				this.filteredJobs.sort((a, b) => {
					const nameA = (a.printer?.name || a.printerName || "").toUpperCase();
					const nameB = (b.printer?.name || b.printerName || "").toUpperCase();

					if (sortDirection === "ASC") {
						return nameA.localeCompare(nameB);
					} else {
						return nameB.localeCompare(nameA);
					}
				});
			}
		},

		/**
		 * Apply filters to the job list
		 */
		applyFilters() {
			// Close date picker if open
			this.dateMenu = false;
			// Always do a full server fetch to get current statuses from API
			this.fetchJobs();
		},

		/**
		 * Clear date range filter
		 */
		clearDateRange() {
			this.dateRange = [];
			this.applyFilters();
		},

		/**
		 * Open job details dialog
		 */
		async viewJobDetails(job) {
			this.selectedJob = job;
			this.jobDetailsDialog = true;
			this.jobStatusHistory = []; // Clear history before fetching

			// First, get the current status of the job
			try {
				const currentStatusResult = await this.apolloClient.query({
					query: gql`
						query GetPrintJobCurrentStatus($printJobId: String!) {
							printJobCurrentStatus(printJobId: $printJobId) {
								status
							}
						}
					`,
					variables: {
						printJobId: job.printJobId,
					},
					fetchPolicy: "network-only",
					errorPolicy: "all",
				});

				// Update the job status with the latest from API
				if (currentStatusResult.data?.printJobCurrentStatus?.status) {
					job.status = currentStatusResult.data.printJobCurrentStatus.status;

					// Update in the main lists as well
					const jobIndex = this.jobs.findIndex((j) => j.printJobId === job.printJobId);
					if (jobIndex >= 0) {
						this.jobs[jobIndex].status = job.status;
					}

					const filteredIndex = this.filteredJobs.findIndex((j) => j.printJobId === job.printJobId);
					if (filteredIndex >= 0) {
						this.filteredJobs[filteredIndex].status = job.status;
					}
				}

				// Now get the status history
				const { data, errors } = await this.apolloClient.query({
					query: gql`
						query GetPrintJobStatusHistory($printJobId: String!) {
							printJobStatusHistory(printJobId: $printJobId) {
								status
								statusString
								timestamp
								isActual
								detailsJson
								statusCode
								isPhase
							}
						}
					`,
					variables: {
						printJobId: job.printJobId,
					},
					fetchPolicy: "network-only",
					errorPolicy: "all",
				});

				if (errors && errors.length > 0) {
					console.error("Error fetching job status history:", errors);
					this.showSnackbar("Failed to fetch job status history", "error");
					return;
				}

				// Get history from the API
				let history = data.printJobStatusHistory || [];

				// Ensure we have at least one history entry
				if (history.length === 0) {
					history.push({
						status: job.status || "Unknown",
						timestamp: job.createdAt,
						isActual: false,
					});
				}

				// Sort history by timestamp to ensure chronological order
				history.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

				// Separate phases (negative status codes) from ezeep statuses (positive codes).
				// Phases should appear in order, then ezeep statuses after the last phase.
				const phases = history.filter((e) => e.isPhase);
				const ezeepStatuses = history.filter((e) => !e.isPhase);

				// Dedup ezeep statuses (remove consecutive duplicates by status text)
				const dedupedEzeep = [];
				let prevEzeepStatus = null;
				for (const entry of ezeepStatuses) {
					if (entry.status !== prevEzeepStatus) {
						dedupedEzeep.push(entry);
						prevEzeepStatus = entry.status;
					}
				}

				// Merge: phases first (in order), then ezeep statuses (in order)
				const mergedHistory = [...phases, ...dedupedEzeep];
				mergedHistory.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

				this.jobStatusHistory = mergedHistory;
			} catch (error) {
				console.error("Error fetching job status history:", error);
				this.showSnackbar("Failed to fetch job status history", "error");
			}
		},

		/**
		 * Check current status of selected job using GraphQL
		 */
		async checkCurrentJobStatus() {
			if (!this.selectedJob) return;

			this.checkingStatus = true;

			// Show a temporary notification
			this.showSnackbar("Checking current job status...", "info", 2000);

			try {
				// Get current status first
				const { data, errors } = await this.apolloClient.query({
					query: gql`
						query GetPrintJobCurrentStatus($printJobId: String!) {
							printJobCurrentStatus(printJobId: $printJobId) {
								status
							}
						}
					`,
					variables: {
						printJobId: this.selectedJob.printJobId,
					},
					// Ensure we always get fresh data from the API
					fetchPolicy: "network-only",
					errorPolicy: "all",
				});

				if (errors && errors.length > 0) {
					console.error("Error checking job status:", errors);
					this.showSnackbar("Failed to check job status", "error");
					this.checkingStatus = false;
					return;
				}

				const newStatus = data.printJobCurrentStatus.status;
				const prevStatus = this.selectedJob.status;

				// Update job status if it changed
				if (prevStatus !== newStatus) {
					// Update selected job status
					this.selectedJob.status = newStatus;

					// Update in the main list as well
					const jobIndex = this.jobs.findIndex((j) => j.printJobId === this.selectedJob.printJobId);
					if (jobIndex >= 0) {
						this.jobs[jobIndex].status = newStatus;
					}

					// Update filtered list as well
					const filteredIndex = this.filteredJobs.findIndex(
						(j) => j.printJobId === this.selectedJob.printJobId
					);
					if (filteredIndex >= 0) {
						this.filteredJobs[filteredIndex].status = newStatus;
					}

					// Re-apply status filtering if needed
					if (this.filters.status) {
						this.applyFilters();
					}

					// Refetch the complete status history to ensure consistency
					try {
						const historyResult = await this.apolloClient.query({
							query: gql`
								query GetPrintJobStatusHistory($printJobId: String!) {
									printJobStatusHistory(printJobId: $printJobId) {
										status
										timestamp
										isActual
										detailsJson
									}
								}
							`,
							variables: {
								printJobId: this.selectedJob.printJobId,
							},
							fetchPolicy: "network-only",
						});

						if (historyResult.data && historyResult.data.printJobStatusHistory) {
							// Apply the same phase/ezeep merge logic as viewJobDetails
							const rawHistory = historyResult.data.printJobStatusHistory;
							const phases = rawHistory.filter((e) => e.isPhase);
							const ezeepStatuses = rawHistory.filter((e) => !e.isPhase);
							const dedupedEzeep = [];
							let prev = null;
							for (const entry of ezeepStatuses) {
								if (entry.status !== prev) {
									dedupedEzeep.push(entry);
									prev = entry.status;
								}
							}
							this.jobStatusHistory = [...phases, ...dedupedEzeep].sort(
								(a, b) => new Date(a.timestamp) - new Date(b.timestamp)
							);
						}
					} catch (historyError) {
						console.error("Error fetching updated job status history:", historyError);
						// Handle history update failure - just add the new status
						if (this.jobStatusHistory && this.jobStatusHistory.length > 0) {
							// Check if last entry status is different from current status
							const lastEntry = this.jobStatusHistory[this.jobStatusHistory.length - 1];
							if (lastEntry.status !== newStatus) {
								// Add new entry with updated status
								this.jobStatusHistory.push({
									status: newStatus,
									timestamp: new Date().toISOString(),
									isActual: true,
									detailsJson: null,
								});
							}
						}
					}

				}

				// Show status update notification
				if (prevStatus !== newStatus) {
					this.showSnackbar(`Job status updated: ${newStatus}`, "success");
				} else {
					this.showSnackbar(`Job status unchanged: ${newStatus}`, "info");
				}
			} catch (error) {
				console.error("Error checking job status:", error);
				this.showSnackbar("Failed to check job status", "error");
			} finally {
				this.checkingStatus = false;
			}
		},

		/**
		 * Retry a failed print job using GraphQL mutation
		 */
		async retryJob(job) {
			try {
				// Store job details dialog state to restore it later if needed
				const wasDialogOpen = this.jobDetailsDialog;
				const selectedJobId = this.selectedJob?.printJobId;

				// Close dialog if open
				if (this.jobDetailsDialog) {
					this.jobDetailsDialog = false;
				}

				const { data, errors } = await this.apolloClient.mutate({
					mutation: gql`
						mutation RetryPrintJob($printJobId: String!) {
							retryPrintJob(printJobId: $printJobId) {
								success
								message
								jobId
							}
						}
					`,
					variables: {
						printJobId: job.printJobId,
					},
					errorPolicy: "all",
				});

				if (errors && errors.length > 0) {
					console.error("Error retrying print job:", errors);
					this.showSnackbar("Failed to retry print job", "error");
					return;
				}

				if (data.retryPrintJob.success) {
					this.showSnackbar("Print job resubmitted successfully", "success");

					// Update job in the list
					const jobIndex = this.jobs.findIndex((j) => j.printJobId === job.printJobId);
					if (jobIndex >= 0) {
						// Update the status to Processing
						this.jobs[jobIndex].status = "Processing";
						this.jobs[jobIndex].message = `Retried: ${data.retryPrintJob.jobId}`;

						// Update in the filtered jobs list as well
						const filteredIndex = this.filteredJobs.findIndex(
							(j) => j.printJobId === job.printJobId
						);
						if (filteredIndex >= 0) {
							this.filteredJobs[filteredIndex].status = "Processing";
							this.filteredJobs[filteredIndex].message = `Retried: ${data.retryPrintJob.jobId}`;
						}

					}

					// If the dialog was showing this job, update the job details
					if (wasDialogOpen && selectedJobId === job.printJobId) {
						// Wait a moment for the backend to process the retry
						setTimeout(() => {
							// Reopen the dialog with updated job
							const updatedJob = this.jobs.find((j) => j.printJobId === job.printJobId);
							if (updatedJob) {
								this.viewJobDetails(updatedJob);
							}
						}, 500);
					}

					// Refresh the list to get the latest status
					this.refreshJobs();
				} else {
					this.showSnackbar(data.retryPrintJob.message || "Failed to retry print job", "error");
				}
			} catch (error) {
				console.error("Error retrying print job:", error);
				this.showSnackbar("Failed to retry print job", "error");
			}
		},

		/**
		 * Retry all selected jobs using GraphQL mutation
		 */
		async retrySelectedJobs() {
			if (this.selectedJobs.length === 0) return;

			try {
				// Only retry failed jobs
				const failedJobs = this.selectedJobs.filter(
					(job) => this.getDisplayStatus(job.status) === "Failed"
				);

				if (failedJobs.length === 0) {
					this.showSnackbar("No failed jobs selected", "info");
					return;
				}

				const printJobIds = failedJobs.map((job) => job.printJobId);

				const { data, errors } = await this.apolloClient.mutate({
					mutation: gql`
						mutation BulkRetryPrintJobs($printJobIds: [String!]!) {
							bulkRetryPrintJobs(printJobIds: $printJobIds) {
								success
								message
							}
						}
					`,
					variables: {
						printJobIds,
					},
					errorPolicy: "all",
				});

				if (errors && errors.length > 0) {
					console.error("Error retrying selected jobs:", errors);
					this.showSnackbar("Failed to retry selected jobs", "error");
					return;
				}

				if (data.bulkRetryPrintJobs.success) {
					this.showSnackbar(
						data.bulkRetryPrintJobs.message || "Print jobs resubmitted successfully",
						"success"
					);

					// Refresh the list to get the latest status
					this.refreshJobs();

					// Clear selection
					this.selectedJobs = [];
				} else {
					this.showSnackbar(
						data.bulkRetryPrintJobs.message || "Failed to retry selected print jobs",
						"error"
					);
				}
			} catch (error) {
				console.error("Error retrying selected jobs:", error);
				this.showSnackbar("Failed to retry some selected jobs", "error");
			}
		},

		/**
		 * Check if a job can be reprinted
		 * Reprint is available for completed jobs with supported document types
		 */
		canReprint(item) {
			if (!item) return false;

			// Check if status allows reprint (Completed or Failed/Error)
			const status = this.getDisplayStatus(item.status);
			const reprintableStatuses = ["Completed", "Failed", "Error"];
			if (!reprintableStatuses.includes(status)) return false;

			// Check if document type supports reprint (has auto-print infrastructure)
			// Note: GraphQL converts enum to SCREAMING_SNAKE_CASE (e.g., "PURCHASE_ORDER")
			const supportedTypes = ["PICK_TICKET", "PURCHASE_ORDER", "PACKING_SLIP"];
			const docType = item.documentType?.toUpperCase?.() || "";

			return supportedTypes.includes(docType);
		},

		/**
		 * Reprint a document by queuing it through the auto-print pipeline
		 */
		async reprintJob(job) {
			try {
				const { data, errors } = await this.apolloClient.mutate({
					mutation: gql`
						mutation ReprintDocument($printJobLogId: Int!) {
							reprintDocument(printJobLogId: $printJobLogId) {
								success
								message
							}
						}
					`,
					variables: {
						printJobLogId: job.printJobLogId,
					},
					errorPolicy: "all",
				});

				if (errors && errors.length > 0) {
					console.error("Error reprinting document:", errors);
					this.showSnackbar("Failed to reprint document: " + errors[0].message, "error");
					return;
				}

				if (data.reprintDocument.success) {
					this.showSnackbar(data.reprintDocument.message || "Document queued for reprint", "success");
					// Refresh the list to show the new print job
					this.refreshJobs();
				} else {
					this.showSnackbar(data.reprintDocument.message || "Failed to reprint document", "error");
				}
			} catch (error) {
				console.error("Error reprinting document:", error);
				this.showSnackbar("Failed to reprint document", "error");
			}
		},

		/**
		 * Handle real-time print job status update from SignalR.
		 * Updates the specific job in-place without a full refetch.
		 */
		handleSignalRUpdate(event) {
			// Check if this job exists in the current list
			const jobIndex = this.jobs.findIndex(
				(j) => j.printJobLogId === event.printJobLogId || j.printJobId === event.printJobId
			);

			if (jobIndex < 0) {
				// New job not in our list — refetch to pick it up
				// Debounce to avoid multiple refetches for a burst of new jobs
				if (this._refetchTimer) clearTimeout(this._refetchTimer);
				this._refetchTimer = setTimeout(() => this.fetchJobs(), 500);
				return;
			}

			const updateJob = (job) => {
				if (!job) return;
				job.status = event.status;
				if (event.totalDurationSeconds != null) job.totalDurationSeconds = event.totalDurationSeconds;
				if (event.timeToSubmitSeconds != null) job.timeToSubmitSeconds = event.timeToSubmitSeconds;
				if (event.ezeepProcessingSeconds != null) job.ezeepProcessingSeconds = event.ezeepProcessingSeconds;
			};

			updateJob(this.jobs[jobIndex]);

			// Update in filtered list
			const filteredIndex = this.filteredJobs.findIndex(
				(j) => j.printJobLogId === event.printJobLogId || j.printJobId === event.printJobId
			);
			if (filteredIndex >= 0) {
				updateJob(this.filteredJobs[filteredIndex]);
			}

			// Update selected job if open in dialog
			if (this.selectedJob &&
				(this.selectedJob.printJobLogId === event.printJobLogId ||
				 this.selectedJob.printJobId === event.printJobId)) {
				updateJob(this.selectedJob);
			}

			// Reapply status filter if active
			if (this.filters.status) {
				this.filteredJobs = this.jobs.filter((job) => {
					const displayStatus = this.getDisplayStatus(job.status);
					return displayStatus === this.filters.status;
				});
			}
		},

		/**
		 * Format a duration in seconds to a human-readable string (e.g., "2m 15s")
		 */
		formatDuration(seconds) {
			if (seconds == null || seconds < 0) return "--";
			seconds = Math.round(seconds);
			if (seconds < 60) return `${seconds}s`;
			const mins = Math.floor(seconds / 60);
			const secs = seconds % 60;
			if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
			const hours = Math.floor(mins / 60);
			const remainMins = mins % 60;
			return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
		},

		/**
		 * Get a user-friendly display name for a lifecycle phase
		 */
		getPhaseDisplayName(status) {
			const phaseNames = {
				Initiated: "Request Initiated",
				Rendering: "Rendering HTML",
				GeneratingPdf: "Generating PDF",
				SubmittingToApi: "Submitting to Printer API",
				Submitted: "Submitted to Printer",
			};
			return phaseNames[status] || status;
		},

		/**
		 * Get icon for a lifecycle phase
		 */
		getPhaseIcon(statusCode) {
			const icons = {
				"-10": "mdi-play-circle-outline",
				"-20": "mdi-code-tags",
				"-30": "mdi-file-pdf-box",
				"-40": "mdi-cloud-upload-outline",
				"-50": "mdi-check-circle-outline",
			};
			return icons[String(statusCode)] || "mdi-circle-small";
		},

		/**
		 * Get color for a lifecycle phase
		 */
		getPhaseColor(statusCode) {
			const colors = {
				"-10": "blue-grey",
				"-20": "indigo",
				"-30": "deep-purple",
				"-40": "teal",
				"-50": "light-blue",
			};
			return colors[String(statusCode)] || "grey";
		},

		/**
		 * Get the time difference in seconds between two status history entries
		 */
		getTimeDiffSeconds(prev, current) {
			if (!prev?.timestamp || !current?.timestamp) return 0;
			return Math.max(0, (new Date(current.timestamp) - new Date(prev.timestamp)) / 1000);
		},

		/**
		 * Get color for time delta chip based on duration
		 */
		getTimeDeltaColor(seconds) {
			if (seconds < 5) return "grey";
			if (seconds < 30) return "orange";
			return "red";
		},

		/**
		 * Format date for display
		 */
		formatDate(dateString, includeTime = false) {
			if (!dateString) return "";

			const date = new Date(dateString);

			if (includeTime) {
				return new Intl.DateTimeFormat("en-US", {
					year: "numeric",
					month: "short",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
				}).format(date);
			}

			return new Intl.DateTimeFormat("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			}).format(date);
		},

		/**
		 * Get color for job status
		 */
		getStatusColor(status) {
			// Get the base status (without details/additional information in parentheses)
			const baseStatus = this.getDisplayStatus(status);

			const statusColors = {
				Loading: "grey",
				Processing: "blue",
				Queued: "orange",
				Printing: "purple",
				Completed: "success",
				Failed: "error",
				Canceled: "grey",
				Unknown: "grey",
				Created: "cyan",
				Error: "deep-orange",
			};

			return statusColors[baseStatus] || "grey";
		},

		/**
		 * Get a simplified status label for display in the status chip
		 */
		getDisplayStatus(status) {
			// If status is undefined or null, return Loading
			if (!status) {
				return "Loading";
			}

			// If the status contains ERROR (case insensitive), categorize as Error
			if (status.toUpperCase().includes("ERROR")) {
				return "Error";
			}

			// If the status is in the format "Status (Details)", extract just the first part
			if (status.includes("(") && status.includes(")")) {
				return status.substring(0, status.indexOf("(")).trim();
			}

			// Otherwise, return the status as is
			return status;
		},

		/**
		 * Get document type name from enum value
		 */
		getDocumentTypeName(type) {
			if (!type) return "Unknown";

			const typeMap = {
				QUOTE: "Quote",
				ORDER: "Sales Order",
				PICK_TICKET: "Pick Ticket",
				PURCHASE_ORDER: "Purchase Order",
				PACKING_SLIP: "Packing Slip",
				OTHER: "Other",
				UNKNOWN: "Unknown",
				// Include original mappings for backward compatibility with existing data
				Quote: "Quote",
				Order: "Sales Order",
				PickTicket: "Pick Ticket",
				PurchaseOrder: "Purchase Order",
				PackingSlip: "Packing Slip",
				Other: "Other",
				Unknown: "Unknown",
			};

			return typeMap[type] || type;
		},

		/**
		 * Get color for document type
		 */
		getDocumentTypeColor(type) {
			if (!type) return "grey";

			const typeColors = {
				QUOTE: "blue",
				ORDER: "green",
				PICK_TICKET: "orange",
				PURCHASE_ORDER: "purple",
				PACKING_SLIP: "indigo",
				OTHER: "grey",
				UNKNOWN: "grey",
				// Include original mappings for backward compatibility with existing data
				Quote: "blue",
				Order: "green",
				PickTicket: "orange",
				PurchaseOrder: "purple",
				PackingSlip: "indigo",
				Other: "grey",
				Unknown: "grey",
			};

			return typeColors[type] || "grey";
		},

		/**
		 * Check if we can navigate to the document
		 */
		canNavigateToDocument(item) {
			// Document must have a valid documentType
			if (!item) return false;

			// Different document types have different required IDs
			const type = item.documentType?.toUpperCase?.() || "";

			switch (type) {
				case "QUOTE":
					return !!item.quoteId; // Need a valid quoteId

				case "ORDER":
					return !!item.orderId; // Need a valid orderId

				case "PICK_TICKET":
					return !!item.orderId && !!item.pickTicketId; // Need both orderId and pickTicketId

				case "PURCHASE_ORDER":
					return !!item.purchaseOrderId; // Need a valid purchaseOrderId

				case "PACKING_SLIP":
					return !!item.orderId && !!item.purchaseOrderId; // Need both orderId and purchaseOrderId

				default:
					return false;
			}
		},

		/**
		 * Navigate to document print template page
		 */
		navigateToDocument(item) {
			const type = item.documentType?.toUpperCase?.() || "";

			switch (type) {
				case "QUOTE":
					// Navigate to quote print template
					this.$router.push(`/sales-orders/quotes/${item.quoteId}/print`);
					break;

				case "ORDER":
					// Navigate to order print template
					this.$router.push(`/sales-orders/orders/${item.orderId}/print`);
					break;

				case "PICK_TICKET":
					// Navigate to pick ticket print template
					this.$router.push(
						`/sales-orders/orders/${item.orderId}/pick-ticket-print?pickTicketIds=${item.pickTicketId}`
					);
					break;

				case "PURCHASE_ORDER":
					// Navigate to purchase order print template
					this.$router.push(`/purchasing/purchase-orders/${item.purchaseOrderId}/print`);
					break;

				case "PACKING_SLIP":
					// Navigate to packing slip print template
					this.$router.push(
						`/sales-orders/orders/${item.orderId}/packing-slip-print?purchaseOrderIds=${item.purchaseOrderId}`
					);
					break;

				default:
					// If unable to determine route, show a message
					this.showSnackbar(
						`Unable to navigate to print template for: ${item.documentNumber}`,
						"info"
					);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.status-dot {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	margin-right: 6px;
}

.v-data-table {
	border-radius: 4px;
	font-size: 14px !important;

	::v-deep th {
		font-size: 15px !important;
		font-weight: 500 !important;
	}

	::v-deep td {
		font-size: 14px !important;
	}
}

.improved-table {
	margin-top: 16px;

	::v-deep .v-data-table__wrapper {
		border: 1px solid rgba(0, 0, 0, 0.12);
	}

	::v-deep thead th {
		background-color: #f5f5f5 !important;
		height: 50px !important;
		color: rgba(0, 0, 0, 0.87) !important;
		font-weight: 600 !important;
		text-transform: none !important;
		white-space: nowrap !important;
	}

	::v-deep tbody tr {
		height: 50px !important;
		cursor: pointer;
	}

	::v-deep tbody tr:hover {
		background-color: rgba(0, 0, 0, 0.04) !important;
	}

	::v-deep .v-data-footer {
		font-size: 14px !important;
		border-top: 1px solid rgba(0, 0, 0, 0.12);
		padding: 8px 16px !important;
	}
}

/* Increase font size for card titles and section headers */
.text-h5 {
	font-size: 1.5rem !important;
	font-weight: 500 !important;
}

.text-subtitle-1,
.text-subtitle-2 {
	font-size: 1rem !important;
}

/* Increase size for filter controls */
::v-deep .v-input__slot {
	font-size: 14px !important;
}

::v-deep .v-label {
	font-size: 14px !important;
}

.larger-input,
.larger-select {
	::v-deep .v-input__slot {
		min-height: 48px !important;
	}

	::v-deep .v-label {
		font-size: 15px !important;
		top: 13px !important;
	}

	::v-deep input,
	::v-deep .v-select__selection {
		font-size: 15px !important;
		padding-top: 3px;
	}
}

::v-deep .v-chip {
	font-size: 13px !important;
}

/* Improve button styling */
.v-btn {
	font-size: 14px !important;
	height: 40px !important;
	font-weight: 500 !important;
	text-transform: none !important;
	letter-spacing: 0.5px !important;
	padding: 0 16px !important;
	border-radius: 4px !important;
}

/* Add shadows and hover effects */
::v-deep .primary {
	box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1) !important;
}

::v-deep .primary:hover {
	box-shadow: 0 5px 8px rgba(0, 0, 0, 0.2) !important;
}

/* Better chip size for document types */
::v-deep .v-chip--small {
	height: 26px !important;
}

/* Style dialog content */
::v-deep .v-dialog .v-card__title {
	font-size: 1.5rem !important;
	font-weight: 500 !important;
}

::v-deep .v-list-item__title {
	font-size: 14px !important;
}

::v-deep .v-list-item__subtitle {
	font-size: 12px !important;
}

/* Document links styling */
.document-link {
	color: #1976d2;
	font-weight: 500;
	text-decoration: none;
	transition: color 0.2s ease;
}

.document-link:hover {
	color: #1565c0;
	text-decoration: underline;
}

.document-link:active {
	color: #0d47a1;
}

/* Action buttons row layout */
.d-flex {
	display: flex !important;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
}

.d-flex .v-btn {
	margin-right: 4px;
}

/* Duration column styling */
.duration-text {
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}
</style>
